import React from "react";
import PropTypes from "prop-types";
import { LinearProgress, ToastContainer, FavIcon } from "@totalsoft/rocket-ui";
import { withTranslation } from "react-i18next";
import { compose, bindActionCreators } from "redux";
import { connect } from "react-redux";
import Header from "./layout/Header";
import Sidebar from "./layout/Sidebar";
import Main from "./layout/Main";
import RightSidebar from "./layout/RightSideBar";
import RightSideBarButtons from "./layout/RightSideBarButtons";
import { loadUserInfo } from "actions/actionCreators/userActions";
import { getApplicationLogo } from "../utils/theme";
import defaultFavIcon from "assets/img/defaultFavIcon.ico";
import AreasProvider from "../providers/AreasProvider";
import { loadGlobalParams } from "features/params/globalParams/actionCreators";
import { ParamsContext } from "../providers/ParamsContext";
import { loadUserRights } from "features/userRights/actionCreators";
import { RightsContext } from "../providers/RightsContext";
import { env } from "utils/env";
import userManager from "utils/userManager";
import IdleTimer from "react-idle-timer";
import { MainPanel, StickySideButtonsContainer, Wrapper } from "./AppStyles";
import ToastNotifier from "./snackbar/ToastNotifier";
import GlobalSubscriptionArea from "features/common/components/GlobalSubscriptionArea";
import GlobalConfirmDialog from "features/common/components/confirm/GlobalConfirmDialog";

class App extends React.PureComponent {
  constructor(props) {
    super(props);
    this.idleTimer = null;
    this.handleOnIdle = this.handleOnIdle.bind(this);
  }

  state = {
    mobileOpen: false,
    miniActive: false,
    mobileRightDrawerOpen: false,
    rightSidebarButtonsVisible: false
  };

  handleOnActive(_event) {
    userManager.signoutRedirect();
  }

  handleOnIdle(_event) {
    console.log("user is idle!");
  }

  mainPanelRef = React.createRef();

  handleDrawerToggle = () => {
    this.setState({ mobileOpen: !this.state.mobileOpen });
  };

  handleRightDrawerToggle = () => {
    this.setState({ mobileRightDrawerOpen: !this.state.mobileRightDrawerOpen });
  };

  showRightSideBar = visible => {
    this.setState({
      mobileRightDrawerOpen: this.state.mobileRightDrawerOpen && visible,
      rightSidebarButtonsVisible: visible
    });
  };

  componentDidMount() {
    if (navigator.platform.indexOf("Win") > -1) {
      // eslint-disable-next-line
      // ps = new PerfectScrollbar(this.mainPanelRef.current, {
      //   suppressScrollX: true,
      //   suppressScrollY: false
      // });
    }
    this.loadUserInfo();
    this.loadGlobalParams();
    this.loadUserRights();
  }

  loadGlobalParams() {
    if (this.props.oidc && this.props.oidc.user) {
      this.props.actions.loadGlobalParams();
    }
  }

  loadUserRights = () => {
    if (this.props.oidc?.user) this.props.actions.loadUserRights();
  };

  componentWillUnmount() {
    if (navigator.platform.indexOf("Win") > -1) {
      //ps.destroy();
    }
  }
  componentDidUpdate(prev) {
    if (!this.props.oidc.isLoadingUser && (prev.oidc !== this.props.oidc || prev.userInfo !== this.props.userInfo)) {
      this.loadUserInfo();
    }

    const params = this.props.params;
    if (prev.params !== params) this.loadGlobalParams();

    const {
      rightsInfo: { loaded: rightsLoaded }
    } = this.props;

    if (prev.rightsInfo.loaded !== rightsLoaded && !rightsLoaded) this.loadUserRights();
  }

  changeLanguage = lng => {
    this.props.i18n.changeLanguage(lng.target.value);
  };

  sidebarMinimize = () => {
    this.setState({ miniActive: !this.state.miniActive });
  };

  loadUserInfo() {
    if (this.props.oidc && this.props.oidc.user) {
      this.props.actions.loadUserInfo();
    }
  }

  render() {
    const {
      loading,
      userInfo,
      rightsInfo: { data: userRights }
    } = this.props;

    return (
      <>
        <ToastNotifier />
        <ToastContainer />
        <AreasProvider>
          <Wrapper>
            {loading && <LinearProgress global />}
            {(userInfo.isLoaded || userInfo.error) && (
              <Sidebar
                handleDrawerToggle={this.handleDrawerToggle}
                changeLanguage={this.changeLanguage}
                open={this.state.mobileOpen}
                miniActive={this.state.miniActive}
              />
            )}
            <MainPanel miniActive={this.state.miniActive} mobileOpen={this.state.mobileOpen} ref={this.mainPanelRef}>
              <FavIcon favIconSource={getApplicationLogo(true)} defaultFavIcon={defaultFavIcon} />
              <Header
                sidebarMinimize={this.sidebarMinimize}
                miniActive={this.state.miniActive}
                handleDrawerToggle={this.handleDrawerToggle}
                handleRightDrawerToggle={this.handleRightDrawerToggle}
                userInfo={userInfo}
              />
              {this.state.rightSidebarButtonsVisible ? (
                <StickySideButtonsContainer>
                  <RightSideBarButtons handleRightDrawerToggle={this.handleRightDrawerToggle} />
                </StickySideButtonsContainer>
              ) : (
                ""
              )}
              <IdleTimer
                crossTab
                ref={ref => {
                  this.idleTimer = ref;
                }}
                timeout={1000 * env.REACT_APP_IDLE_TIMEOUT}
                onIdle={this.handleOnIdle}
                onActive={this.handleOnActive}
                debounce={500}
              />
              <ParamsContext.Provider value={this.props.params}>
                <RightsContext.Provider value={userRights}>{userRights.rights && <Main />}</RightsContext.Provider>
              </ParamsContext.Provider>
            </MainPanel>

            <RightSidebar
              handleRightDrawerToggle={this.handleRightDrawerToggle}
              showRightSideBar={this.showRightSideBar}
              open={this.state.mobileRightDrawerOpen}
            />
            <GlobalSubscriptionArea />
            <GlobalConfirmDialog />
          </Wrapper>
        </AreasProvider>
      </>
    );
  }
}

App.propTypes = {
  loading: PropTypes.bool,
  i18n: PropTypes.object.isRequired,
  oidc: PropTypes.object.isRequired,
  params: PropTypes.object,
  userInfo: PropTypes.object,
  actions: PropTypes.object,
  rightsInfo: PropTypes.object
};

function mapStateToProps(state) {
  return {
    loading: state.ajaxCallsInProgress > 0,
    oidc: state.oidc,
    userInfo: state.userInfo,
    params: state.params,
    rightsInfo: state.userRights
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadUserInfo,
        loadGlobalParams,
        loadUserRights
      },
      dispatch
    ),
    dispatch
  };
}

export default compose(withTranslation(), connect(mapStateToProps, mapDispatchToProps))(App);
